import { FC, useEffect, useState } from 'react';
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import queryString from 'query-string';

import { routes } from '~src/routingConfig';
import PageLoader from '~src/shared/pageLoader/PageLoader';
import { baseBlue } from '~src/styles/colors';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';
import { itemIdMatch } from '~src/constants';
import { CardsContainer } from '~src/modules/questions/components/shared.sc';
import { Centering } from '~src/modules/questions/containers/questions.sc';
import { ComponentRouterContainer as ObjectivesRouterContainer } from '~src/styles/shared.sc';
import { ObjectiveI, ObjectivesResponse } from '~src/store/okrs/keyResultStream/interfaces';
import { fetchObjectivesA } from '~src/store/okrs/actions';
import { ObjectiveFilterTypes, ObjectivesQueryI } from '~src/store/okrs/interfaces';
import { mapIdToData } from '~src/utils';
import PageNotFound from '~src/shared/errorPages/PageNotFound';

import KeyResultsStreamContainer from './keyResultsStream/KeyResultsStreamContainer';
import NoObjectiveSuper from './noObjective/NoObjective';
import FinishedObjectiveStream from './objectiveUpdated/FinishedObjectiveStream';

interface StateProps {
  objectives: { [key: string]: ObjectiveI };
}

interface DispatchProps {
  fetchObjectives: (query?: ObjectivesQueryI) => ApiReduxResp<ObjectivesResponse>;
}

type Props = DispatchProps & StateProps & RouteComponentProps<any>;

const ObjectivesRouter: FC<Props> = props => {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getObjectives().finally(() => {
      setLoading(false);
    });
  }, []);

  const getObjectives = async () => {
    try {
      const { response, error } = await props.fetchObjectives({ [ObjectiveFilterTypes.Stream]: 'true' });
      if (error) {
        throw new Error('error fetching objectives list');
      }
      return response;
    } catch (err) {
      // todo: show user appropriate error screen
    }
  };

  // if the url doesnt contain objectiveId
  const defaultRoute = (): JSX.Element => {
    let redirectPath = routes.okrs.objectivesStream.noObjective;
    const objectivesIds = Object.keys(props.objectives);
    if (objectivesIds.length > 0) {
      // redirect to the first objective update krs stream screen
      redirectPath = `${props.match.url}/${objectivesIds[0]}`;
    }
    return <Redirect to={redirectPath} />;
  };

  const renderFinished = () => {
    const finishedObjectiveId = queryString.parse(props.location.search).OID as string;
    const nextObjectiveId = queryString.parse(props.location.search).NOID as string;
    return <FinishedObjectiveStream objectiveId={finishedObjectiveId} nextObjectiveId={nextObjectiveId} />;
  };

  const renderNoObjective = () => {
    return <NoObjectiveSuper />;
  };

  return (
    <ObjectivesRouterContainer>
      {loading ? (
        <CardsContainer>
          <Centering>
            <PageLoader secondColor={baseBlue.a5} />
          </Centering>
        </CardsContainer>
      ) : (
        <Switch>
          {Object.keys(props.objectives).length > 0 && (
            <Route
              exact
              path={`${routes.okrs.objectivesStream.updateObjectiveKRs}/:objectiveId(${itemIdMatch})`}
              component={KeyResultsStreamContainer}
            />
          )}
          {/* todo: error handling */}
          {/* todo: noObjective */}
          <Route path={routes.okrs.objectivesStream.finished} render={renderFinished} />
          <Route exact path={routes.okrs.objectivesStream.noObjective} render={renderNoObjective} />
          <Route exact path={routes.okrs.objectivesStream.updateObjectiveKRs} render={defaultRoute} />
          <PageNotFound />
        </Switch>
      )}
    </ObjectivesRouterContainer>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  objectives: state.okrs.objectives.objectivesListData
    ? mapIdToData(state.okrs.objectives.objectivesListData.items)
    : {},
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchObjectives: query => dispatch(fetchObjectivesA(query)),
});

const ObjectivesRouterWithRouter = withRouter(ObjectivesRouter);
const ObjectivesRouterSuper = connect(mapStateToProps, mapDispatchToProps)(ObjectivesRouterWithRouter);

export default ObjectivesRouterSuper;
